import { Check } from "lucide-react";
import { useState } from "react";

import { ClaimAgentOutreachDialog } from "@/components/ClaimAgentOutreachDialog";
import { OwnerAvatars } from "@/components/OwnerAvatars";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useTaggableUsers, useUserOptionsWithSelected } from "@/contexts/TaggableUsersContext";
import { useCreateCandidacyOwner } from "@/hooks/mutations/useCreateCandidacyOwner";
import { useDeleteCandidacyOwner } from "@/hooks/mutations/useDeleteCandidacyOwner";
import { useStartStafferAgentConversation } from "@/hooks/mutations/useStartStafferAgentConversation";
import { useGetJobInternalConfig } from "@/hooks/queries/useGetJobInternalConfig";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";
import { CandidacyStatus } from "@/types";

export const CandidacyOwnerCell = ({
  candidacyId,
  owners,
  readOnly = false,
  candidacyStatus,
  promptAgentOnClaim = false,
  onAgentStarted,
  agentClaimScreen = MIXPANEL_SCREENS.JOB,
  showTooltip = false,
}: {
  candidacyId: string;
  owners: { id: number; userId: number }[];
  readOnly?: boolean;
  candidacyStatus?: CandidacyStatus;
  promptAgentOnClaim?: boolean;
  onAgentStarted?: () => void;
  agentClaimScreen?: MIXPANEL_SCREENS;
  showTooltip?: boolean;
}) => {
  /* Database CRUD Operations */
  // Read
  const { data: jobInternalConfig } = useGetJobInternalConfig();

  // Update
  const { mutate: createCandidacyOwner } = useCreateCandidacyOwner();
  const { mutate: deleteCandidacyOwner } = useDeleteCandidacyOwner();
  const { mutate: startStafferAgent, isPending: isStartingAgent } =
    useStartStafferAgentConversation();

  /* React Hooks */
  const { userInitialsMap, userNameMap } = useTaggableUsers();
  const [open, setOpen] = useState(false);
  const [showAgentClaimDialog, setShowAgentClaimDialog] = useState(false);

  // Ownership is single-select: at most one owner.
  const currentOwner = owners.at(0);
  const ownerUserIds = currentOwner ? [currentOwner.userId] : [];
  const userOptions = useUserOptionsWithSelected(ownerUserIds);

  /* Handlers */
  const handleSelect = (userId: number) => {
    setOpen(false);
    // Clicking the current owner toggles them off (single-select unassign).
    if (currentOwner?.userId === userId) {
      deleteCandidacyOwner({ candidacyId, id: currentOwner.id });
      return;
    }

    // Prompt to start the agent only when claiming a previously unowned
    // shortlist candidacy on an agent-on job.
    const shouldPromptForAgent =
      promptAgentOnClaim &&
      !currentOwner &&
      candidacyStatus === CandidacyStatus.SHORTLIST &&
      jobInternalConfig?.stafferAgentActive === true;

    createCandidacyOwner(
      {
        candidacyId,
        createCandidacyOwnerRequest: {
          currentOwner: currentOwner?.userId,
          newOwner: userId,
        },
      },
      {
        onSuccess: () => {
          if (shouldPromptForAgent) setShowAgentClaimDialog(true);
        },
      },
    );
  };

  /* Render */
  const renderTrigger = () => {
    const trigger = (
      <PopoverTrigger asChild>
        {/* Using a raw <div> because Row doesn't forward refs, which Radix asChild requires. */}
        <div
          className="flex cursor-pointer items-center gap-1"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <OwnerAvatars
            ownerUserIds={ownerUserIds}
            userInitialsMap={userInitialsMap}
          />
        </div>
      </PopoverTrigger>
    );

    if (!showTooltip) return trigger;

    const ownerName = currentOwner ? userNameMap[currentOwner.userId] : null;
    return (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>{trigger}</TooltipTrigger>
          <TooltipContent>
            {ownerName ? `Owner · ${ownerName}` : "Assign owner"}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const render = () => {
    if (readOnly) {
      return (
        <OwnerAvatars
          ownerUserIds={ownerUserIds}
          userInitialsMap={userInitialsMap}
        />
      );
    }

    return (
      <>
        <Popover onOpenChange={setOpen} open={open}>
          {renderTrigger()}
          <PopoverContent
            align="start"
            className="w-56 p-0"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <Command>
              <CommandList>
                <CommandGroup>
                  {userOptions.map((option) => {
                    const isSelected = option.value === currentOwner?.userId;
                    return (
                      <CommandItem
                        key={String(option.value)}
                        onSelect={() => {
                          handleSelect(option.value);
                        }}
                      >
                        <Check className={cn(!isSelected && "invisible")} />
                        {option.label}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <ClaimAgentOutreachDialog
          candidacyId={candidacyId}
          isLoading={isStartingAgent}
          onOpenChange={setShowAgentClaimDialog}
          screenName={agentClaimScreen}
          onUseAgent={() => {
            startStafferAgent(candidacyId, {
              onSuccess: () => {
                setShowAgentClaimDialog(false);
                onAgentStarted?.();
              },
            });
          }}
          open={showAgentClaimDialog}
        />
      </>
    );
  };

  return render();
};
